/**
 * ContractsExpirySettings — panneau paramètres pour alertes fin de contrat.
 * Placé dans /app/contracts/parametres (via ContratsParametres.jsx qui
 * l'importe). L'utilisateur choisit :
 *   - Activer/désactiver les rappels
 *   - Les seuils J-N (défauts 90/60/30 — jusqu'à 8 valeurs)
 *   - Des emails additionnels (ex: gestionnaire ou comptable externe)
 */
import { useEffect, useState } from "react";
import api, { apiError } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { X, Plus, Bell, Mail, Save, Loader2 } from "lucide-react";
import { toast } from "sonner";

const PRESETS = [90, 60, 30, 14, 7];

export default function ContractsExpirySettings() {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [days, setDays] = useState([90, 60, 30]);
  const [emails, setEmails] = useState([]);
  const [newDay, setNewDay] = useState("");
  const [newEmail, setNewEmail] = useState("");

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/contracts/expiry/settings");
        setEnabled(data.enabled ?? true);
        setDays(data.reminder_days || [90, 60, 30]);
        setEmails(data.extra_emails || []);
      } catch (e) { toast.error(apiError(e)); }
      finally { setLoading(false); }
    })();
  }, []);

  const addDay = (value) => {
    const n = parseInt(value, 10);
    if (!n || n < 1 || n > 365) return toast.error("Entre 1 et 365 jours");
    if (days.includes(n)) return;
    setDays([...days, n].sort((a, b) => b - a).slice(0, 8));
    setNewDay("");
  };
  const removeDay = (n) => setDays(days.filter((d) => d !== n));

  const addEmail = () => {
    const e = newEmail.trim();
    if (!e || !e.includes("@")) return toast.error("Email invalide");
    if (emails.includes(e)) return;
    setEmails([...emails, e]);
    setNewEmail("");
  };
  const removeEmail = (e) => setEmails(emails.filter((x) => x !== e));

  const save = async () => {
    setSaving(true);
    try {
      await api.patch("/contracts/expiry/settings", {
        enabled, reminder_days: days, extra_emails: emails,
      });
      toast.success("Paramètres d'alertes sauvegardés");
    } catch (e) { toast.error(apiError(e)); }
    finally { setSaving(false); }
  };

  if (loading) return (
    <Card><CardContent className="p-6 flex justify-center">
      <Loader2 className="h-5 w-5 animate-spin text-slate-400" />
    </CardContent></Card>
  );

  return (
    <Card data-testid="contract-expiry-settings">
      <CardContent className="p-6 space-y-5">
        <div className="flex items-start justify-between gap-4 pb-4 border-b">
          <div>
            <h3 className="text-base font-semibold text-slate-900 flex items-center gap-2">
              <Bell className="h-4 w-4 text-blue-600" />
              Alertes fin de contrat
            </h3>
            <p className="text-xs text-slate-500 mt-1">
              Prévient automatiquement l'équipe X jours avant la fin d'un contrat actif pour renégocier ou résilier à temps.
            </p>
          </div>
          <Switch checked={enabled} onCheckedChange={setEnabled}
                  data-testid="expiry-enabled-switch" />
        </div>

        <div className={enabled ? "" : "opacity-40 pointer-events-none"}>
          {/* Seuils */}
          <Label className="text-xs font-medium text-slate-700 mb-2 block">
            Seuils de rappel (jours avant échéance)
          </Label>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {days.map((d) => (
              <Badge key={d} variant="secondary"
                      className="bg-blue-50 text-blue-700 border-blue-200 pr-1"
                      data-testid={`expiry-day-${d}`}>
                J-{d}
                <button onClick={() => removeDay(d)} className="ml-1.5 hover:text-red-600">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
            {days.length === 0 && (
              <span className="text-[11px] text-slate-400 italic">Aucun seuil — les alertes ne partiront pas</span>
            )}
          </div>
          <div className="flex items-center gap-2 mb-1">
            <Input type="number" placeholder="Ex: 45"
                    value={newDay} onChange={(e) => setNewDay(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addDay(newDay)}
                    className="h-8 w-32 text-sm"
                    data-testid="expiry-add-day-input" />
            <Button size="sm" variant="outline" onClick={() => addDay(newDay)}
                    data-testid="expiry-add-day-btn">
              <Plus className="h-3 w-3 mr-1" /> Ajouter
            </Button>
            <div className="flex gap-1.5 ml-auto">
              {PRESETS.filter((p) => !days.includes(p)).map((p) => (
                <button key={p} onClick={() => addDay(p)}
                        className="text-[11px] px-2 py-0.5 rounded border border-slate-200 text-slate-500 hover:bg-slate-50">
                  + J-{p}
                </button>
              ))}
            </div>
          </div>

          {/* Emails supplémentaires */}
          <Label className="text-xs font-medium text-slate-700 mb-2 block mt-5 flex items-center gap-1.5">
            <Mail className="h-3 w-3" />
            Destinataires additionnels <span className="font-normal text-slate-500">(optionnel)</span>
          </Label>
          <p className="text-[11px] text-slate-500 mb-2">
            En plus des dirigeants/CFO/managers du tenant, ces emails externes recevront aussi l'alerte (ex: gestionnaire du portefeuille, comptable externe).
          </p>
          <div className="flex flex-wrap gap-1.5 mb-2">
            {emails.map((e) => (
              <Badge key={e} variant="secondary"
                      className="bg-slate-100 text-slate-700 pr-1"
                      data-testid={`expiry-email-${e}`}>
                {e}
                <button onClick={() => removeEmail(e)} className="ml-1.5 hover:text-red-600">
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            ))}
          </div>
          <div className="flex items-center gap-2">
            <Input type="email" placeholder="email@exemple.fr"
                    value={newEmail} onChange={(e) => setNewEmail(e.target.value)}
                    onKeyDown={(e) => e.key === "Enter" && addEmail()}
                    className="h-8 flex-1 max-w-xs text-sm"
                    data-testid="expiry-add-email-input" />
            <Button size="sm" variant="outline" onClick={addEmail}
                    data-testid="expiry-add-email-btn">
              <Plus className="h-3 w-3 mr-1" /> Ajouter
            </Button>
          </div>
        </div>

        <div className="flex justify-end pt-4 border-t">
          <Button onClick={save} disabled={saving}
                  className="bg-blue-600 hover:bg-blue-700"
                  data-testid="expiry-save-btn">
            {saving ? <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />
                     : <Save className="h-4 w-4 mr-1.5" />}
            Enregistrer
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
